/**
 * Extraction Context Module
 *
 * Provides extraction state management across the Evidence Extractor application.
 * Handles extraction results, review progress tracking, and pending feedback
 * with state preservation across page navigation.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 10.2, 10.3
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isDemoMode } from '../config';
import { ApiClient, createApiClient, isApiSuccess, getErrorMessage } from '../services/apiClient';
import { getMockDataService, type MockDataService } from '../services/mockDataService';
import {
  parseContentProcessorResponse,
  isValidContentProcessorResponse,
  parseResultObject,
} from '../services/responseParser';
import type { ExtractionResults, FeedbackSubmission, Feedback, FeedbackItemType } from '../types';
import type { ContentResultUpdate, ContentCommentUpdate } from '../types/api';

// ============================================================================
// Types
// ============================================================================

/**
 * Review progress tracking interface
 * Tracks current page, completed pages, and unsaved changes state
 */
export interface ReviewProgress {
  /** Current page number (1-4) */
  currentPage: number;
  /** Array of page numbers that have been marked as complete */
  completedPages: number[];
  /** Whether there are unsaved feedback changes */
  hasUnsavedChanges: boolean;
}

/**
 * Extraction context value interface
 * Provides extraction state and operations to consuming components
 */
export interface ExtractionContextValue {
  /** Current extraction results being reviewed */
  currentExtraction: ExtractionResults | null;
  /** Review progress tracking state */
  reviewProgress: ReviewProgress;
  /** Map of pending feedback submissions (itemId -> FeedbackSubmission) */
  pendingFeedback: Map<string, FeedbackSubmission>;
  /** Map of stored feedback with full metadata (itemId -> Feedback) */
  storedFeedback: Map<string, Feedback>;
  /** Whether extraction data is currently being loaded */
  isLoading: boolean;
  /** Error that occurred during the last operation, if any */
  error: Error | null;
  /** Set the current extraction results */
  setCurrentExtraction: (extraction: ExtractionResults | null) => void;
  /**
   * Load extraction results by process ID
   * Fetches from /contentprocessor/processed/{process_id} and transforms response
   * (Requirement 8.1, 8.2, 8.4)
   */
  loadExtraction: (processId: string) => Promise<void>;
  /**
   * Update extraction result data
   * Calls PUT /contentprocessor/processed/{process_id} with modified results
   * (Requirement 8.3)
   */
  updateExtractionResult: (processId: string, result: Record<string, unknown>) => Promise<void>;
  /**
   * Add a comment to an extraction
   * Calls PUT /contentprocessor/processed/{process_id} with comment update
   * (Requirement 8.3)
   */
  addComment: (processId: string, comment: string) => Promise<void>;
  /**
   * Add or update feedback for an item
   * Stores feedback with timestamp and user identifier (Requirements 8.2, 8.3)
   */
  addFeedback: (feedback: FeedbackSubmission, itemType: FeedbackItemType, originalValue: string) => void;
  /** Remove pending feedback for an item */
  removeFeedback: (itemId: string) => void;
  /** Save all pending feedback to the server */
  saveFeedback: () => Promise<void>;
  /** Mark a page as complete */
  markPageComplete: (page: number) => void;
  /** Mark a page as incomplete */
  markPageIncomplete: (page: number) => void;
  /** Navigate to a specific page */
  navigateToPage: (page: number) => void;
  /** Check if all pages are complete */
  isReviewComplete: () => boolean;
  /** Clear all pending feedback */
  clearPendingFeedback: () => void;
  /** Reset the entire extraction context */
  resetContext: () => void;
  /** Clear any existing error */
  clearError: () => void;
  /** Get feedback for a specific item */
  getFeedback: (itemId: string) => Feedback | undefined;
  /**
   * Fetch extraction results by process ID without storing them in context.
   * Used by VersionComparison to load two extractions independently.
   */
  fetchExtraction: (processId: string) => Promise<ExtractionResults | null>;
}

/**
 * Props for the ExtractionProvider component
 */
interface ExtractionProviderProps {
  /** Child components to wrap with the context */
  children: ReactNode;
}

// ============================================================================
// Constants
// ============================================================================

/** Total number of review pages */
const TOTAL_PAGES = 4;

/** Page labels for the review interface */
export const PAGE_LABELS = [
  'Ground Risk Answers',
  'Ground Risk Mitigations',
  'Air Risk Answers',
  'Air Risk Mitigations',
];

/** Initial review progress state */
const INITIAL_REVIEW_PROGRESS: ReviewProgress = {
  currentPage: 1,
  completedPages: [],
  hasUnsavedChanges: false,
};

// ============================================================================
// Context Creation
// ============================================================================

/**
 * Extraction context with undefined default value
 * Must be used within an ExtractionProvider
 */
const ExtractionContext = createContext<ExtractionContextValue | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

/**
 * ExtractionProvider component
 *
 * Provides extraction state management to child components.
 * Handles state preservation across page navigation and integrates
 * with API client or mock service based on demo mode.
 *
 * @param props - Provider props containing children
 * @returns Provider component wrapping children
 */
export function ExtractionProvider({ children }: ExtractionProviderProps): React.ReactElement {
  // State
  const [currentExtraction, setCurrentExtractionState] = useState<ExtractionResults | null>(null);
  const [reviewProgress, setReviewProgress] = useState<ReviewProgress>(INITIAL_REVIEW_PROGRESS);
  const [pendingFeedback, setPendingFeedback] = useState<Map<string, FeedbackSubmission>>(new Map());
  const [storedFeedback, setStoredFeedback] = useState<Map<string, Feedback>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Auth0 hook for API authentication and user info
  const { getAccessTokenSilently, user } = useAuth0();

  // Memoized services
  const apiClient = useMemo<ApiClient | null>(() => {
    if (isDemoMode()) {
      return null;
    }
    return createApiClient(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  const mockService = useMemo<MockDataService | null>(() => {
    if (isDemoMode()) {
      return getMockDataService();
    }
    return null;
  }, []);

  /**
   * Set the current extraction results
   * Resets review progress when a new extraction is set
   */
  const setCurrentExtraction = useCallback((extraction: ExtractionResults | null): void => {
    setCurrentExtractionState(extraction);

    // Reset review progress when setting a new extraction
    if (extraction) {
      setReviewProgress({
        currentPage: 1,
        completedPages: [],
        hasUnsavedChanges: false,
      });
      // Clear pending and stored feedback for new extraction
      setPendingFeedback(new Map());
      setStoredFeedback(new Map());
    }
  }, []);

  /**
   * Load extraction results by process ID
   *
   * Fetches from /contentprocessor/processed/{process_id} and uses the
   * Response_Parser to transform the response to ExtractionResults.
   *
   * Requirements: 8.1, 8.2, 8.4
   *
   * @param processId - The process ID to load extraction results for
   */
  const loadExtraction = useCallback(async (processId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      if (isDemoMode() && mockService) {
        // Demo mode: use mock data
        const extraction = mockService.getExtractionResultsById(processId);
        if (extraction) {
          setCurrentExtractionState(extraction);
          setReviewProgress({
            currentPage: 1,
            completedPages: [],
            hasUnsavedChanges: false,
          });
          setPendingFeedback(new Map());
          setStoredFeedback(new Map());
        } else {
          throw new Error(`Extraction not found: ${processId}`);
        }
      } else if (apiClient) {
        // Production mode: call API using the new processed endpoint
        // Requirement 8.1: Fetch from /contentprocessor/processed/{process_id}
        const response = await apiClient.getProcessedDocument(processId);

        if (isApiSuccess(response)) {
          // Requirement 8.2: Use Response_Parser to transform the response
          // The apiClient.getProcessedDocument already uses parseContentProcessResponse
          // to transform the ContentProcessResponse to ProcessedDocument.
          // Now we need to convert ProcessedDocument to ExtractionResults format.

          const processedDoc = response.data;
          const documentName = processedDoc.processedFileName ?? 'Unknown Document';

          // Requirement 8.4: Handle result and extracted_comparison_data fields
          // Parse the result object to extract ground/air risk data
          const parsedResults = parseResultObject(
            processedDoc.result as Record<string, unknown> | null,
            documentName
          );

          // If result parsing didn't yield data, try extracted_comparison_data as fallback
          let groundRiskAnswers = parsedResults.groundRiskAnswers;
          let groundRiskMitigations = parsedResults.groundRiskMitigations;
          let airRiskAnswers = parsedResults.airRiskAnswers;
          let airRiskMitigations = parsedResults.airRiskMitigations;

          // Fallback to extracted_comparison_data if result parsing yielded no data
          if (
            groundRiskAnswers.length === 0 &&
            groundRiskMitigations.length === 0 &&
            airRiskAnswers.length === 0 &&
            airRiskMitigations.length === 0 &&
            processedDoc.extractedComparisonData &&
            processedDoc.extractedComparisonData.length > 0
          ) {
            // Create a ContentProcessorResponse-like object for the legacy parser
            const contentProcessorResponse = {
              process_id: processedDoc.processId,
              processed_file_name: processedDoc.processedFileName ?? '',
              processed_file_mime_type: processedDoc.processedFileMimeType ?? '',
              processed_time: processedDoc.processedTime ?? '',
              imported_time: processedDoc.importedTime,
              last_modified_time: processedDoc.lastModifiedTime,
              last_modified_by: processedDoc.lastModifiedBy ?? '',
              status: processedDoc.status,
              entity_score: processedDoc.entityScore,
              min_extracted_entity_score: processedDoc.minExtractedEntityScore,
              schema_score: processedDoc.schemaScore,
              result: processedDoc.result ? JSON.stringify(processedDoc.result) : '',
              confidence: typeof processedDoc.confidence === 'object' && processedDoc.confidence !== null
                ? (processedDoc.confidence as Record<string, number>)['overall'] ?? 0
                : 0,
              target_schema: processedDoc.targetSchema?.className ?? '',
              prompt_tokens: processedDoc.promptTokens,
              completion_tokens: processedDoc.completionTokens,
              process_output: processedDoc.processOutput
                .map(step => `${step.stepName}: ${JSON.stringify(step.stepResult)}`)
                .join('\n'),
              extracted_comparison_data: processedDoc.extractedComparisonData.map(item => ({
                Field: item.field,
                Extracted: item.extracted,
                Confidence: item.confidence,
                IsAboveThreshold: item.isAboveThreshold,
              })),
            };

            // Validate and parse using legacy parser
            if (isValidContentProcessorResponse(contentProcessorResponse)) {
              const legacyExtraction = parseContentProcessorResponse(contentProcessorResponse);
              groundRiskAnswers = legacyExtraction.groundRiskAnswers;
              groundRiskMitigations = legacyExtraction.groundRiskMitigations;
              airRiskAnswers = legacyExtraction.airRiskAnswers;
              airRiskMitigations = legacyExtraction.airRiskMitigations;
            }
          }

          // Build the ExtractionResults object
          const extraction: ExtractionResults = {
            processId: processedDoc.processId,
            processedFileName: processedDoc.processedFileName ?? '',
            processedFileMimeType: processedDoc.processedFileMimeType ?? '',
            processedTime: processedDoc.processedTime ?? '',
            importedTime: processedDoc.importedTime,
            lastModifiedTime: processedDoc.lastModifiedTime,
            lastModifiedBy: processedDoc.lastModifiedBy ?? '',
            status: processedDoc.status,
            entityScore: processedDoc.entityScore,
            minExtractedEntityScore: processedDoc.minExtractedEntityScore,
            schemaScore: processedDoc.schemaScore,
            result: processedDoc.result ? JSON.stringify(processedDoc.result) : '',
            confidence: typeof processedDoc.confidence === 'object' && processedDoc.confidence !== null
              ? (processedDoc.confidence as Record<string, number>)['overall'] ?? 0
              : 0,
            targetSchema: processedDoc.targetSchema?.className ?? '',
            promptTokens: processedDoc.promptTokens,
            completionTokens: processedDoc.completionTokens,
            processOutput: processedDoc.processOutput
              .map(step => `${step.stepName}: ${JSON.stringify(step.stepResult)}`)
              .join('\n'),
            groundRiskAnswers,
            groundRiskMitigations,
            airRiskAnswers,
            airRiskMitigations,
          };

          setCurrentExtractionState(extraction);

          setReviewProgress({
            currentPage: 1,
            completedPages: [],
            hasUnsavedChanges: false,
          });
          setPendingFeedback(new Map());
          setStoredFeedback(new Map());
        } else {
          throw new Error(getErrorMessage(response.error));
        }
      } else {
        throw new Error('No extraction service available. Please check your configuration.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load extraction';
      setError(new Error(errorMessage));
      console.error('Error loading extraction:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, mockService]);

  /**
   * Fetch extraction results by process ID without storing in context.
   * Returns the ExtractionResults directly for use in comparison views.
   */
  const fetchExtraction = useCallback(async (processId: string): Promise<ExtractionResults | null> => {
    try {
      if (isDemoMode() && mockService) {
        const extraction = mockService.getExtractionResultsById(processId);
        return extraction || null;
      } else if (apiClient) {
        const response = await apiClient.getProcessedDocument(processId);
        if (isApiSuccess(response)) {
          const processedDoc = response.data;
          const documentName = processedDoc.processedFileName ?? 'Unknown Document';
          const parsedResults = parseResultObject(
            processedDoc.result as Record<string, unknown> | null,
            documentName
          );

          let groundRiskAnswers = parsedResults.groundRiskAnswers;
          let groundRiskMitigations = parsedResults.groundRiskMitigations;
          let airRiskAnswers = parsedResults.airRiskAnswers;
          let airRiskMitigations = parsedResults.airRiskMitigations;

          if (
            groundRiskAnswers.length === 0 &&
            groundRiskMitigations.length === 0 &&
            airRiskAnswers.length === 0 &&
            airRiskMitigations.length === 0 &&
            processedDoc.extractedComparisonData &&
            processedDoc.extractedComparisonData.length > 0
          ) {
            const contentProcessorResponse = {
              process_id: processedDoc.processId,
              processed_file_name: processedDoc.processedFileName ?? '',
              processed_file_mime_type: processedDoc.processedFileMimeType ?? '',
              processed_time: processedDoc.processedTime ?? '',
              imported_time: processedDoc.importedTime,
              last_modified_time: processedDoc.lastModifiedTime,
              last_modified_by: processedDoc.lastModifiedBy ?? '',
              status: processedDoc.status,
              entity_score: processedDoc.entityScore,
              min_extracted_entity_score: processedDoc.minExtractedEntityScore,
              schema_score: processedDoc.schemaScore,
              result: processedDoc.result ? JSON.stringify(processedDoc.result) : '',
              confidence: typeof processedDoc.confidence === 'object' && processedDoc.confidence !== null
                ? (processedDoc.confidence as Record<string, number>)['overall'] ?? 0
                : 0,
              target_schema: processedDoc.targetSchema?.className ?? '',
              prompt_tokens: processedDoc.promptTokens,
              completion_tokens: processedDoc.completionTokens,
              process_output: processedDoc.processOutput
                .map(step => `${step.stepName}: ${JSON.stringify(step.stepResult)}`)
                .join('\n'),
              extracted_comparison_data: processedDoc.extractedComparisonData.map(item => ({
                Field: item.field,
                Extracted: item.extracted,
                Confidence: item.confidence,
                IsAboveThreshold: item.isAboveThreshold,
              })),
            };

            if (isValidContentProcessorResponse(contentProcessorResponse)) {
              const legacyExtraction = parseContentProcessorResponse(contentProcessorResponse);
              groundRiskAnswers = legacyExtraction.groundRiskAnswers;
              groundRiskMitigations = legacyExtraction.groundRiskMitigations;
              airRiskAnswers = legacyExtraction.airRiskAnswers;
              airRiskMitigations = legacyExtraction.airRiskMitigations;
            }
          }

          return {
            processId: processedDoc.processId,
            processedFileName: processedDoc.processedFileName ?? '',
            processedFileMimeType: processedDoc.processedFileMimeType ?? '',
            processedTime: processedDoc.processedTime ?? '',
            importedTime: processedDoc.importedTime,
            lastModifiedTime: processedDoc.lastModifiedTime,
            lastModifiedBy: processedDoc.lastModifiedBy ?? '',
            status: processedDoc.status,
            entityScore: processedDoc.entityScore,
            minExtractedEntityScore: processedDoc.minExtractedEntityScore,
            schemaScore: processedDoc.schemaScore,
            result: processedDoc.result ? JSON.stringify(processedDoc.result) : '',
            confidence: typeof processedDoc.confidence === 'object' && processedDoc.confidence !== null
              ? (processedDoc.confidence as Record<string, number>)['overall'] ?? 0
              : 0,
            targetSchema: processedDoc.targetSchema?.className ?? '',
            promptTokens: processedDoc.promptTokens,
            completionTokens: processedDoc.completionTokens,
            processOutput: processedDoc.processOutput
              .map(step => `${step.stepName}: ${JSON.stringify(step.stepResult)}`)
              .join('\n'),
            groundRiskAnswers,
            groundRiskMitigations,
            airRiskAnswers,
            airRiskMitigations,
          };
        }
        return null;
      }
      return null;
    } catch (err) {
      console.error('Error fetching extraction:', err);
      return null;
    }
  }, [apiClient, mockService]);

  /**
   * Update extraction result data
   *
   * Calls PUT /contentprocessor/processed/{process_id} with modified results.
   * Uses ContentResultUpdate payload format.
   *
   * Requirement 8.3: updateExtractionResult() calls update endpoint with modified results
   *
   * @param processId - The process ID of the extraction to update
   * @param result - The modified result data to save
   */
  const updateExtractionResult = useCallback(async (
    processId: string,
    result: Record<string, unknown>
  ): Promise<void> => {
    setError(null);

    try {
      if (isDemoMode()) {
        // Demo mode: simulate update delay
        await new Promise((resolve) => setTimeout(resolve, 500));
        console.log('Demo mode: Updated extraction result:', { processId, result });

        // Update local state to reflect the change
        if (currentExtraction && currentExtraction.processId === processId) {
          setCurrentExtractionState({
            ...currentExtraction,
            result: JSON.stringify(result),
            lastModifiedTime: new Date().toISOString(),
          });
        }
      } else if (apiClient) {
        // Production mode: call API with ContentResultUpdate payload
        const update: ContentResultUpdate = {
          process_id: processId,
          modified_result: result,
        };

        const response = await apiClient.updateProcessedDocument(processId, update);

        if (isApiSuccess(response)) {
          // Reload the extraction to get the updated data
          await loadExtraction(processId);
        } else {
          throw new Error(getErrorMessage(response.error));
        }
      } else {
        throw new Error('No extraction service available. Please check your configuration.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update extraction result';
      setError(new Error(errorMessage));
      console.error('Error updating extraction result:', err);
      throw err; // Re-throw so callers can handle it
    }
  }, [apiClient, currentExtraction, loadExtraction]);

  /**
   * Add a comment to an extraction
   *
   * Calls PUT /contentprocessor/processed/{process_id} with comment update.
   * Uses ContentCommentUpdate payload format.
   *
   * Requirement 8.3: addComment() calls update endpoint with comment
   *
   * @param processId - The process ID of the extraction to update
   * @param comment - The comment text to add
   */
  const addComment = useCallback(async (
    processId: string,
    comment: string
  ): Promise<void> => {
    setError(null);

    try {
      if (isDemoMode()) {
        // Demo mode: simulate update delay
        await new Promise((resolve) => setTimeout(resolve, 500));
        console.log('Demo mode: Added comment to extraction:', { processId, comment });

        // Update local state to reflect the change (if we had a comment field in ExtractionResults)
        // For now, just log the action
      } else if (apiClient) {
        // Production mode: call API with ContentCommentUpdate payload
        const update: ContentCommentUpdate = {
          process_id: processId,
          comment: comment,
        };

        const response = await apiClient.updateProcessedDocument(processId, update);

        if (isApiSuccess(response)) {
          // Reload the extraction to get the updated data
          await loadExtraction(processId);
        } else {
          throw new Error(getErrorMessage(response.error));
        }
      } else {
        throw new Error('No extraction service available. Please check your configuration.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add comment';
      setError(new Error(errorMessage));
      console.error('Error adding comment:', err);
      throw err; // Re-throw so callers can handle it
    }
  }, [apiClient, loadExtraction]);

  /**
   * Add or update feedback for an item
   * Stores feedback with timestamp and user identifier (Requirements 8.2, 8.3)
   * Preserves state across page navigation (Requirement 10.2)
   *
   * @param feedback - The feedback submission to add
   * @param itemType - The type of item (ground_risk_answer, air_risk_mitigation, etc.)
   * @param originalValue - The original extracted value being corrected
   */
  const addFeedback = useCallback((
    feedback: FeedbackSubmission,
    itemType: FeedbackItemType,
    originalValue: string
  ): void => {
    // Store the basic feedback submission for API calls
    setPendingFeedback((prev) => {
      const newMap = new Map(prev);
      newMap.set(feedback.itemId, feedback);
      return newMap;
    });

    // Store the full feedback record with timestamp and user identifier (Requirement 8.3)
    const fullFeedback: Feedback = {
      id: `feedback-${feedback.itemId}-${Date.now()}`,
      extractionId: currentExtraction?.processId || '',
      itemId: feedback.itemId,
      itemType,
      originalValue,
      correctedValue: feedback.correctedValue,
      isCorrect: feedback.isCorrect,
      comment: feedback.comment,
      submittedAt: new Date().toISOString(),
      submittedBy: user?.sub || user?.email || 'anonymous',
    };

    setStoredFeedback((prev) => {
      const newMap = new Map(prev);
      newMap.set(feedback.itemId, fullFeedback);
      return newMap;
    });

    // Mark that there are unsaved changes
    setReviewProgress((prev) => ({
      ...prev,
      hasUnsavedChanges: true,
    }));
  }, [currentExtraction?.processId, user]);

  /**
   * Remove pending feedback for an item
   *
   * @param itemId - The item ID to remove feedback for
   */
  const removeFeedback = useCallback((itemId: string): void => {
    setPendingFeedback((prev) => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });

    setStoredFeedback((prev) => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });

    // Update unsaved changes flag based on remaining feedback
    setPendingFeedback((current) => {
      setReviewProgress((prev) => ({
        ...prev,
        hasUnsavedChanges: current.size > 0,
      }));
      return current;
    });
  }, []);

  /**
   * Save all pending feedback to the server
   * Called when completing the review (Requirement 10.5)
   */
  const saveFeedback = useCallback(async (): Promise<void> => {
    if (pendingFeedback.size === 0) {
      return;
    }

    if (!currentExtraction) {
      throw new Error('No extraction loaded to save feedback for');
    }

    setError(null);

    try {
      if (isDemoMode()) {
        // Demo mode: simulate save delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Log the stored feedback for demo purposes
        console.log('Demo mode: Saving feedback with full metadata:',
          Array.from(storedFeedback.values())
        );

        // Clear pending and stored feedback after successful save
        setPendingFeedback(new Map());
        setStoredFeedback(new Map());
        setReviewProgress((prev) => ({
          ...prev,
          hasUnsavedChanges: false,
        }));
      } else if (apiClient) {
        // Production mode: call API with batch feedback
        const feedbackItems = Array.from(pendingFeedback.values());
        const response = await apiClient.submitBatchFeedback(
          currentExtraction.processId,
          feedbackItems
        );

        if (isApiSuccess(response)) {
          // Clear pending and stored feedback after successful save
          setPendingFeedback(new Map());
          setStoredFeedback(new Map());
          setReviewProgress((prev) => ({
            ...prev,
            hasUnsavedChanges: false,
          }));
        } else {
          throw new Error(getErrorMessage(response.error));
        }
      } else {
        throw new Error('No feedback service available. Please check your configuration.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save feedback';
      setError(new Error(errorMessage));
      console.error('Error saving feedback:', err);
      throw err; // Re-throw so callers can handle it
    }
  }, [apiClient, currentExtraction, pendingFeedback, storedFeedback]);

  /**
   * Mark a page as complete
   * Updates progress indicator (Requirement 10.3)
   *
   * @param page - The page number to mark as complete (1-4)
   */
  const markPageComplete = useCallback((page: number): void => {
    if (page < 1 || page > TOTAL_PAGES) {
      console.warn(`Invalid page number: ${page}. Must be between 1 and ${TOTAL_PAGES}`);
      return;
    }

    setReviewProgress((prev) => {
      // Don't add duplicate pages
      if (prev.completedPages.includes(page)) {
        return prev;
      }

      return {
        ...prev,
        completedPages: [...prev.completedPages, page].sort((a, b) => a - b),
      };
    });
  }, []);

  /**
   * Mark a page as incomplete
   *
   * @param page - The page number to mark as incomplete (1-4)
   */
  const markPageIncomplete = useCallback((page: number): void => {
    if (page < 1 || page > TOTAL_PAGES) {
      console.warn(`Invalid page number: ${page}. Must be between 1 and ${TOTAL_PAGES}`);
      return;
    }

    setReviewProgress((prev) => ({
      ...prev,
      completedPages: prev.completedPages.filter((p) => p !== page),
    }));
  }, []);

  /**
   * Navigate to a specific page
   * Preserves pending feedback across navigation (Requirement 10.2)
   *
   * @param page - The page number to navigate to (1-4)
   */
  const navigateToPage = useCallback((page: number): void => {
    if (page < 1 || page > TOTAL_PAGES) {
      console.warn(`Invalid page number: ${page}. Must be between 1 and ${TOTAL_PAGES}`);
      return;
    }

    // State is preserved automatically - just update current page
    setReviewProgress((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  /**
   * Check if all pages are complete
   * Used to enable/disable "Complete Review" action (Requirement 10.4)
   *
   * @returns true if all pages are marked as complete
   */
  const isReviewComplete = useCallback((): boolean => {
    return reviewProgress.completedPages.length === TOTAL_PAGES;
  }, [reviewProgress.completedPages]);

  /**
   * Clear all pending feedback
   */
  const clearPendingFeedback = useCallback((): void => {
    setPendingFeedback(new Map());
    setStoredFeedback(new Map());
    setReviewProgress((prev) => ({
      ...prev,
      hasUnsavedChanges: false,
    }));
  }, []);

  /**
   * Get feedback for a specific item
   * Returns the full feedback record with timestamp and user identifier
   *
   * @param itemId - The item ID to get feedback for
   * @returns The feedback record or undefined if not found
   */
  const getFeedback = useCallback((itemId: string): Feedback | undefined => {
    return storedFeedback.get(itemId);
  }, [storedFeedback]);

  /**
   * Reset the entire extraction context
   */
  const resetContext = useCallback((): void => {
    setCurrentExtractionState(null);
    setReviewProgress(INITIAL_REVIEW_PROGRESS);
    setPendingFeedback(new Map());
    setStoredFeedback(new Map());
    setError(null);
  }, []);

  /**
   * Clear any existing error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Memoized context value
  const contextValue = useMemo<ExtractionContextValue>(() => ({
    currentExtraction,
    reviewProgress,
    pendingFeedback,
    storedFeedback,
    isLoading,
    error,
    setCurrentExtraction,
    loadExtraction,
    updateExtractionResult,
    addComment,
    addFeedback,
    removeFeedback,
    saveFeedback,
    markPageComplete,
    markPageIncomplete,
    navigateToPage,
    isReviewComplete,
    clearPendingFeedback,
    resetContext,
    clearError,
    getFeedback,
    fetchExtraction,
  }), [
    currentExtraction,
    reviewProgress,
    pendingFeedback,
    storedFeedback,
    isLoading,
    error,
    setCurrentExtraction,
    loadExtraction,
    updateExtractionResult,
    addComment,
    addFeedback,
    removeFeedback,
    saveFeedback,
    markPageComplete,
    markPageIncomplete,
    navigateToPage,
    isReviewComplete,
    clearPendingFeedback,
    resetContext,
    clearError,
    getFeedback,
    fetchExtraction,
  ]);

  return (
    <ExtractionContext.Provider value={contextValue}>
      {children}
    </ExtractionContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the extraction context
 *
 * @returns ExtractionContextValue with extraction state and operations
 * @throws Error if used outside of ExtractionProvider
 *
 * @example
 * ```tsx
 * function ReviewPage() {
 *   const {
 *     currentExtraction,
 *     reviewProgress,
 *     pendingFeedback,
 *     addFeedback,
 *     markPageComplete,
 *     navigateToPage,
 *     isReviewComplete,
 *     saveFeedback,
 *   } = useExtraction();
 *
 *   const handleComplete = async () => {
 *     if (isReviewComplete()) {
 *       await saveFeedback();
 *       // Navigate to completion page
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <ReviewNavigation
 *         currentPage={reviewProgress.currentPage}
 *         completedPages={reviewProgress.completedPages}
 *         onNavigate={navigateToPage}
 *       />
 *       // Review content here
 *     </div>
 *   );
 * }
 * ```
 */
export function useExtraction(): ExtractionContextValue {
  const context = useContext(ExtractionContext);

  if (context === undefined) {
    throw new Error('useExtraction must be used within an ExtractionProvider');
  }

  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { ExtractionContext, TOTAL_PAGES };
export default ExtractionProvider;
